export const MODE = {
    UNDEFINED: 0,
    PRODUCING: 1,
    IDLE: 2,
    MANEUVERING: 3,
    TRAVELING: 4,
    OFF: 5,
    WAITING_FOR_DATA: 6,
    DATA_LOST: 7,
    MAINTENANCE: 8
};

const COUNTABLE_MODES = [MODE.PRODUCING, MODE.IDLE, MODE.MANEUVERING, MODE.TRAVELING, MODE.OFF, MODE.MAINTENANCE];
const ENGINE_ON_MODES = [MODE.PRODUCING, MODE.IDLE, MODE.MANEUVERING, MODE.TRAVELING];
const PRODUCTIVE_MODES = [MODE.PRODUCING, MODE.MANEUVERING, MODE.TRAVELING];

const SECONDS_PER_HOUR = 3600;
const SQUARE_METERS_PER_HECTARE = 10000;
const PERCENT = 100;

export function isCountableMode(mode) {
    return COUNTABLE_MODES.includes(mode);
}

export function isEngineOnMode(mode) {
    return ENGINE_ON_MODES.includes(mode);
}

export function isProductiveMode(mode) {
    return PRODUCTIVE_MODES.includes(mode);
}

function isMaintenanceMode(mode) {
    return mode === MODE.MAINTENANCE;
}

export function normalizeEvent(event) {
    return {
        ...event,
        time: event.time / SECONDS_PER_HOUR,
        area: event.area / SQUARE_METERS_PER_HECTARE
    };
}

export function normalizeEvents(events) {
    return events.map(normalizeEvent);
}

export function countableEvents(events) {
    return events.filter(event => isCountableMode(event.mode));
}

export function eventsByMode(events, mode) {
    return events.filter(event => event.mode === mode);
}

function sumField(events, field, includesMode) {
    let total = 0;

    events.forEach(event => {
        if (!includesMode(event.mode)) {
            return;
        }

        if (!Number.isFinite(event[field])) {
            return;
        }

        total += event[field];
    });

    return total;
}

export function totalTime(events) {
    return sumField(events, 'time', isCountableMode);
}

export function monitoredPeriod(events) {
    return totalTime(events);
}

export function engineOnTime(events) {
    return sumField(events, 'time', isEngineOnMode);
}

export function productiveHours(events) {
    return sumField(events, 'time', isProductiveMode);
}

export function maintenanceTime(events) {
    return sumField(events, 'time', isMaintenanceMode);
}

function availableTime(events) {
    return monitoredPeriod(events) - maintenanceTime(events);
}

export function totalFuel(events) {
    return sumField(events, 'consumption', isCountableMode);
}

export function engineOnFuel(events) {
    return sumField(events, 'consumption', isEngineOnMode);
}

export function area(events) {
    return sumField(events, 'area', isCountableMode);
}

export function distance(events) {
    return sumField(events, 'distance', isCountableMode);
}

export function productiveDistance(events) {
    return sumField(events, 'distance', isProductiveMode);
}

function createRatio({numerator, denominator, scale = 1}) {
    function calculate(events) {
        let denominatorValue = denominator(events);

        if (denominatorValue <= 0) {
            return NaN;
        }

        return (numerator(events) / denominatorValue) * scale;
    }

    function parts(event) {
        return {
            numerator: numerator([event]) * scale,
            denominator: denominator([event])
        };
    }

    calculate.parts = parts;

    return calculate;
}

export const fuelRateByTime = createRatio({numerator: engineOnFuel, denominator: engineOnTime});
export const fuelRateByArea = createRatio({numerator: distance, denominator: totalFuel});
export const fuelPerHectare = createRatio({numerator: totalFuel, denominator: area});
export const speed = createRatio({numerator: productiveDistance, denominator: productiveHours});
export const productivity = createRatio({numerator: area, denominator: productiveHours});
export const efficiency = createRatio({numerator: productiveHours, denominator: monitoredPeriod, scale: PERCENT});
export const mechanicalAvailability = createRatio({numerator: availableTime, denominator: monitoredPeriod, scale: PERCENT});

function sumFieldByMode(events, field) {
    let totals = {};

    COUNTABLE_MODES.forEach(mode => {
        totals[mode] = sumField(events, field, eventMode => eventMode === mode);
    });

    return totals;
}

export function timeByMode(events) {
    return sumFieldByMode(events, 'time');
}

export function fuelByMode(events) {
    return sumFieldByMode(events, 'consumption');
}

export function summarize(events) {
    return {
        totalTime: totalTime(events),
        monitoredPeriod: monitoredPeriod(events),
        engineOnTime: engineOnTime(events),
        productiveHours: productiveHours(events),
        maintenanceTime: maintenanceTime(events),
        totalFuel: totalFuel(events),
        area: area(events),
        distance: distance(events),
        fuelRateByTime: fuelRateByTime(events),
        fuelRateByArea: fuelRateByArea(events),
        fuelPerHectare: fuelPerHectare(events),
        speed: speed(events),
        productivity: productivity(events),
        efficiency: efficiency(events),
        mechanicalAvailability: mechanicalAvailability(events)
    };
}
